import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { EdMark } from "./EdLogo";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const chips = ["Tráfego Pago", "Apps", "IA", "Design", "Web"];

const Hero = () => {
  const cardRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 60,
    damping: 18,
  });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 60,
    damping: 18,
  });

  const onMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section className="relative overflow-hidden pb-16 pt-28 sm:pt-36 lg:pb-28">
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-6 flex items-center gap-3 font-display text-[11px] font-bold uppercase tracking-[0.35em] text-brand-neon"
          >
            <span className="h-px w-8 bg-brand-neon/60" />
            Growth / Design / Web / AI
          </motion.p>

          <h1 className="font-display text-[8.5vw] font-extrabold leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl">
            <Line delay={0.2}>TECNOLOGIA</Line>
            <Line delay={0.32}>E ESTRATÉGIA</Line>
            <Line delay={0.44}>PARA O CRESCIMENTO</Line>
            <Line delay={0.56}>
              <span className="text-brand-neon drop-shadow-[0_0_28px_rgba(168,85,247,0.55)]">
                DO SEU NEGÓCIO.
              </span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-6 max-w-md text-sm leading-relaxed text-mist sm:text-base"
          >
            Criamos sites profissionais, aplicativos e soluções digitais
            que valorizam sua marca e conectam sua empresa a novas
            oportunidades.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink(WA_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-main-button"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-wa px-7 py-4 font-display text-sm font-bold text-ink transition-transform duration-300 hover:scale-[1.03] active:scale-95"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.4} />
              Falar no WhatsApp
              <span className="absolute inset-0 -translate-x-full bg-paper/25 transition-transform duration-500 group-hover:translate-x-0" />
            </a>
            <span className="flex items-center gap-2 text-xs font-medium text-mist">
              <span className="relative flex h-2 w-2">
                <span className="absolute h-full w-full rounded-full bg-mint animate-ping-slow" />
                <span className="h-2 w-2 rounded-full bg-mint" />
              </span>
              Disponível para novos projetos
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="relative mx-auto w-full max-w-sm [perspective:1200px]"
        >
          <motion.div
            ref={cardRef}
            onMouseMove={onMove}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            style={{ rotateX: rotX, rotateY: rotY }}
            className="glass glow-card relative flex aspect-square flex-col items-center justify-center rounded-[2rem] p-8"
            data-testid="hero-brand-card"
          >
            <div className="absolute inset-6 rounded-[1.6rem] border border-brand-neon/10" />
            <EdMark className="w-40 drop-shadow-[0_0_45px_rgba(124,58,237,0.6)] sm:w-48" />
            <span className="mt-6 font-display text-lg font-extrabold tracking-[0.5em] text-paper">
              LABS
            </span>
            <span className="mt-2 text-[10px] uppercase tracking-[0.3em] text-mist">
              Growth &amp; Technology
            </span>
            <div className="pointer-events-none absolute inset-0 rounded-[2rem] bg-[radial-gradient(circle_at_50%_0%,rgba(168,85,247,0.14),transparent_55%)]" />
          </motion.div>

          {chips.map((chip, i) => (
            <motion.span
              key={chip}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 + i * 0.12, ease: EASE }}
              className={`glass absolute z-10 rounded-full px-3.5 py-1.5 text-[11px] font-semibold text-paper animate-floaty ${
                [
                  "-top-3 left-8",
                  "top-1/3 -right-5",
                  "-bottom-4 left-12",
                  "bottom-14 -left-6",
                  "top-5 -right-4",
                ][i]
              }`}
              style={{ animationDelay: `${i * 1.3}s` }}
            >
              {chip}
            </motion.span>
          ))}
        </motion.div>
      </div>

      <motion.a
        href="#servicos"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="mx-auto mt-14 flex w-max items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-mist transition-colors hover:text-brand-neon"
      >
        explore
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
      </motion.a>
    </section>
  );
};

export default Hero;
